import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { CustomFieldValueMap } from './customFields';

// Treatment plans v2. Mirrors the server shape from `format()` in
// [server/src/routes/tx_plans.js]. Plans reference live client-level
// behaviors/programs via join tables — no snapshot is taken, so plan detail
// responses always reflect the current operational definitions.

export type TxPlanStatus = 'Draft' | 'Signed' | 'Active' | 'Archived';

export interface TxPlanBehaviorRef {
    id: string;
    name: string;
    status: string;
    behaviorType: string;
}

export interface TxPlanProgramTargetRef {
    id: string;
    name: string;
    description: string;
    dataType: 'trial' | 'percent' | 'frequency' | 'duration' | 'task_analysis';
    masteryDirection: 'at_or_above' | 'at_or_below';
    masteryValue: number | null;
    masterySessions: number | null;
    trials: number | null;
    chainingMethod: 'total_task' | 'forward' | 'backward' | null;
    status: string;
}

export interface TxPlanProgramRef {
    id: string;
    name: string;
    status: string;
    teachingMethod: string;
    programDomain: string;
    targets: TxPlanProgramTargetRef[];
}

export interface TxPlan {
    id: string;
    clientId: string;
    title: string;
    startDate: string | null;
    reviewDate: string | null;
    bcbaId: string | null;
    bcbaName: string;
    status: TxPlanStatus;
    notes: string;
    signedAtBcba: string | null;
    signedNameBcba: string;
    signedAtParent: string | null;
    signedNameParent: string;
    signedRelationshipParent: string;
    sentToPortalAt: string | null;
    customFields: CustomFieldValueMap;
    signedAt: string | null;
    createdAt: string;
    updatedAt: string;
    behaviors: TxPlanBehaviorRef[];
    programs: TxPlanProgramRef[];
}

export interface TxPlanWritePayload {
    clientId?: string;
    title?: string;
    startDate?: string | null;
    reviewDate?: string | null;
    bcbaId?: string | null;
    notes?: string | null;
    behaviorIds?: string[];
    programIds?: string[];
    customFields?: CustomFieldValueMap;
}

export interface SignaturePayload {
    signerName: string;
    signatureImage: string; // base64 / data-URL; server decodes
}

export interface ParentSignaturePayload extends SignaturePayload {
    relationship: string;
}

interface ListOpts {
    clientId: string;
    status?: TxPlanStatus;
    includeArchived?: boolean;
}

async function fetchTxPlans(opts: ListOpts): Promise<TxPlan[]> {
    const params: Record<string, string> = { clientId: opts.clientId };
    if (opts.status) params.status = opts.status;
    if (opts.includeArchived) params.includeArchived = '1';
    const res = await api.get<TxPlan[]>('/tx-plans', { params });
    return res.data;
}

export function useTxPlans(opts: ListOpts) {
    return useQuery({
        queryKey: [
            'tx-plans',
            opts.clientId,
            opts.status ?? 'any',
            opts.includeArchived ? 'with-archived' : 'no-archived',
        ] as const,
        queryFn: () => fetchTxPlans(opts),
        enabled: !!opts.clientId,
        staleTime: 30_000,
    });
}

async function fetchTxPlan(id: string): Promise<TxPlan> {
    const res = await api.get<TxPlan>(`/tx-plans/${id}`);
    return res.data;
}

export function useTxPlan(id: string | undefined) {
    return useQuery({
        queryKey: ['tx-plans', 'entry', id] as const,
        queryFn: () => fetchTxPlan(id!),
        enabled: !!id,
    });
}

export function useCreateTxPlan(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: TxPlanWritePayload) => {
            const res = await api.post<TxPlan>('/tx-plans', { ...payload, clientId });
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['tx-plans', clientId] }),
    });
}

export function useUpdateTxPlan(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: TxPlanWritePayload }) => {
            const res = await api.patch<TxPlan>(`/tx-plans/${id}`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['tx-plans', clientId] });
            qc.invalidateQueries({ queryKey: ['tx-plans', 'entry', vars.id] });
        },
    });
}

export function useDeleteTxPlan(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/tx-plans/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['tx-plans', clientId] }),
    });
}

export function useTransitionTxPlan(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, status }: { id: string; status: TxPlanStatus }) => {
            const res = await api.post<TxPlan>(`/tx-plans/${id}/transition`, { status });
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['tx-plans', clientId] });
            qc.invalidateQueries({ queryKey: ['tx-plans', 'entry', vars.id] });
            qc.invalidateQueries({ queryKey: ['client-programs', clientId] });
        },
    });
}

export function useSignBcba(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: SignaturePayload }) => {
            const res = await api.post<TxPlan>(`/tx-plans/${id}/sign-bcba`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['tx-plans', clientId] });
            qc.invalidateQueries({ queryKey: ['tx-plans', 'entry', vars.id] });
        },
    });
}

export function useSignParent(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ParentSignaturePayload }) => {
            const res = await api.post<TxPlan>(`/tx-plans/${id}/sign-parent`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['tx-plans', clientId] });
            qc.invalidateQueries({ queryKey: ['tx-plans', 'entry', vars.id] });
        },
    });
}

export function useSendPlanToPortal(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<TxPlan>(`/tx-plans/${id}/send-to-portal`);
            return res.data;
        },
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: ['tx-plans', clientId] });
            qc.invalidateQueries({ queryKey: ['tx-plans', 'entry', id] });
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}

export function useRecallPlanFromPortal(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<TxPlan>(`/tx-plans/${id}/recall-from-portal`);
            return res.data;
        },
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: ['tx-plans', clientId] });
            qc.invalidateQueries({ queryKey: ['tx-plans', 'entry', id] });
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}

export function signatureImageUrl(planId: string, kind: 'bcba' | 'parent'): string {
    return `/api/tx-plans/${planId}/signatures/${kind}/image`;
}

// Export a plan as .docx against a chosen template. Returns the blob and a
// suggested filename parsed out of Content-Disposition (server sanitizes
// title + appends `.docx`). Caller is responsible for triggering the browser
// download (createObjectURL + anchor click) and revoking the URL.
export async function exportTxPlanDocx(
    planId: string,
    templateId: string,
): Promise<{ blob: Blob; filename: string }> {
    try {
        const res = await api.post(
            `/tx-plans/${planId}/export`,
            { templateId },
            { responseType: 'blob' },
        );
        const cd = String(res.headers['content-disposition'] ?? '');
        const match = /filename="?([^"]+)"?/i.exec(cd);
        const filename = match ? match[1] : 'treatment-plan.docx';
        return { blob: res.data as Blob, filename };
    } catch (err) {
        // responseType: 'blob' means axios hands us a Blob on error too.
        // Re-parse as JSON so callers see the server's `{ error: '...' }` message.
        const anyErr = err as { response?: { data?: Blob } };
        const data = anyErr?.response?.data;
        if (data instanceof Blob) {
            let message: string | null = null;
            try {
                const text = await data.text();
                message = (JSON.parse(text) as { error?: string })?.error ?? null;
            } catch {
                message = null;
            }
            if (message) throw new Error(message);
        }
        throw err;
    }
}

// Pre-selection helper for the plan builder. Mirrors the stakeholder rule:
// "default to all currently-active behaviors and programs for the client".
// Pure function so we can vitest it without mounting the builder.
export function defaultPlanMembership(
    behaviors: { id: string; status: string }[],
    programs: { id: string; status: string }[],
): { behaviorIds: string[]; programIds: string[] } {
    return {
        behaviorIds: behaviors.filter((b) => b.status === 'Active').map((b) => b.id),
        programIds:  programs.filter((p) => p.status === 'Active').map((p) => p.id),
    };
}
